/**
 * Хук для управления фотографиями посадки с поддержкой offline-режима
 */

import { useCallback, useEffect, useState } from 'react';
import { useDeletePlantingPhotoMutation } from '../api/plantingsApi';
import {
  savePhotoLocally,
  getPhotosByPlanting,
  deletePhoto,
  createPhotoUrl,
  revokePhotoUrl,
  // updatePhotoStatus, // Not used currently
  type PhotoRecord,
} from '../utils/imageStorage';
import { compressImage, createThumbnail, isImageFile, validateFileSize } from '../utils/imageCompression';
import { photoSyncService } from '../services/photoSyncService';
import type { PlantingPhoto } from '../models/Planting';

export interface LocalPhotoWithUrl extends PhotoRecord {
  previewUrl: string;
}

export interface UsePlantingPhotosResult {
  localPhotos: LocalPhotoWithUrl[];
  remotePhotos: PlantingPhoto[];
  isLoading: boolean;
  addPhoto: (file: File, description?: string) => Promise<void>;
  removeLocalPhoto: (photoId: string) => Promise<void>;
  removeRemotePhoto: (photoId: string | number) => Promise<void>;
  syncPhotos: () => void;
  refreshLocalPhotos: () => Promise<void>;
}

export function usePlantingPhotos(saveId: string, plantingId: string | number): UsePlantingPhotosResult {
  const [localPhotos, setLocalPhotos] = useState<LocalPhotoWithUrl[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  
  // const [uploadPhotoApi] = useUploadPlantingPhotoMutation(); // Sync service handles uploads
  const [deletePhotoApi] = useDeletePlantingPhotoMutation();

  /**
   * Загрузка локальных фото из IndexedDB
   */
  const loadLocalPhotos = useCallback(async () => {
    try {
      const photos = await getPhotosByPlanting(plantingId);
      const photosWithUrls: LocalPhotoWithUrl[] = photos.map((photo) => ({
        ...photo,
        previewUrl: createPhotoUrl(photo.thumbnailBlob || photo.blob),
      }));
      setLocalPhotos(photosWithUrls);
    } catch (error) {
      console.error('Ошибка загрузки локальных фото:', error);
    }
  }, [plantingId]);

  /**
   * Добавление нового фото
   */
  const addPhoto = useCallback(
    async (file: File, description?: string) => {
      setIsLoading(true);
      
      try {
        // Валидация
        if (!isImageFile(file)) {
          throw new Error('Файл должен быть изображением');
        }
        
        if (!validateFileSize(file, 10)) {
          throw new Error('Размер файла не должен превышать 10 MB');
        }

        // Сжатие изображения
        const compressedFile = await compressImage(file);
        const thumbnail = await createThumbnail(file);

        // Сохранение в локальное хранилище
        await savePhotoLocally(plantingId, compressedFile, thumbnail, {
          saveId,
          description,
        });

        // Обновление списка
        await loadLocalPhotos();

        // Попытка загрузки, если есть интернет
        if (navigator.onLine) {
          photoSyncService.sync();
        }
      } catch (error) {
        console.error('Ошибка добавления фото:', error);
        throw error;
      } finally {
        setIsLoading(false);
      }
    },
    [plantingId, loadLocalPhotos, saveId]
  );

  /**
   * Удаление локального фото
   */
  const removeLocalPhoto = useCallback(
    async (photoId: string) => {
      try {
        // Находим фото чтобы освободить URL
        const photo = localPhotos.find((p) => p.id === photoId);
        if (photo) {
          revokePhotoUrl(photo.previewUrl);
        }

        await deletePhoto(photoId);
        await loadLocalPhotos();
      } catch (error) {
        console.error('Ошибка удаления локального фото:', error);
        throw error;
      }
    },
    [localPhotos, loadLocalPhotos]
  );

  /**
   * Удаление удалённого фото (с сервера)
   */
  const removeRemotePhoto = useCallback(
    async (photoId: string | number) => {
      try {
        await deletePhotoApi({ saveId, plantingId, photoId }).unwrap();
      } catch (error) {
        console.error('Ошибка удаления удалённого фото:', error);
        throw error;
      }
    },
    [plantingId, deletePhotoApi, saveId]
  );

  /**
   * Принудительная синхронизация
   */
  const syncPhotos = useCallback(() => {
    photoSyncService.sync(true); // true = принудительная попытка, игнорируем лимит попыток
  }, []);

  /**
   * Обновление локальных фото
   */
  const refreshLocalPhotos = useCallback(async () => {
    await loadLocalPhotos();
  }, [loadLocalPhotos]);

  /**
   * Загрузка локальных фото при монтировании
   */
  useEffect(() => {
    loadLocalPhotos();

    // Подписка на события синхронизации
    const unsubscribe = photoSyncService.subscribe((event) => {
      if (event.type === 'photo-uploaded' || event.type === 'completed') {
        loadLocalPhotos();
      }
    });

    return () => {
      // Освобождаем URLs при размонтировании
      localPhotos.forEach((photo) => {
        revokePhotoUrl(photo.previewUrl);
      });
      unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plantingId, loadLocalPhotos]);

  return {
    localPhotos,
    remotePhotos: [], // Заполняется из API query
    isLoading,
    addPhoto,
    removeLocalPhoto,
    removeRemotePhoto,
    syncPhotos,
    refreshLocalPhotos,
  };
}
